"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerLanguageAndSuggestions = exports.SQLEditor = void 0;
const tslib_1 = require("tslib");
const ui_1 = require("@grafana/ui");
const react_1 = tslib_1.__importStar(require("react"));
const getStatementPosition_1 = require("../standardSql/getStatementPosition");
const getStandardSuggestions_1 = require("../standardSql/getStandardSuggestions");
const suggestionsKindRegistry_1 = require("../standardSql/suggestionsKindRegistry");
const types_1 = require("../types");
const getSuggestionKind_1 = require("../utils/getSuggestionKind");
const linkedTokenBuilder_1 = require("../utils/linkedTokenBuilder");
const tokenUtils_1 = require("../utils/tokenUtils");
const commands_1 = require("../utils/commands");
const uuid_1 = require("uuid");
const data_1 = require("@grafana/data");
const standardSuggestionsRegistry_1 = require("../standardSql/standardSuggestionsRegistry");
const statementPositionResolversRegistry_1 = require("../standardSql/statementPositionResolversRegistry");
const debugger_1 = require("../utils/debugger");
const definition_1 = tslib_1.__importDefault(require("../standardSql/definition"));
const standardSQLCompletionItemProvider_1 = require("../standardSql/standardSQLCompletionItemProvider");
const STANDARD_SQL_LANGUAGE = 'sql';
const LANGUAGES_CACHE = new Map();
const INSTANCE_CACHE = new Map();
const SQLEditor = ({ children, onBlur, onChange, query, language = { id: STANDARD_SQL_LANGUAGE }, width, height, }) => {
    const monacoRef = (0, react_1.useRef)(null);
    const langUid = (0, react_1.useRef)();
    // create unique language id for each SQLEditor instance
    const id = (0, react_1.useMemo)(() => {
        const uid = (0, uuid_1.v4)();
        const id = `${language.id}-${uid}`;
        langUid.current = id;
        return id;
    }, [language.id]);
    (0, react_1.useEffect)(() => {
        return () => {
            if (langUid.current) {
                INSTANCE_CACHE.delete(langUid.current);
            }
            (0, debugger_1.sqlEditorLog)(`Removing instance cache ${langUid.current}`, false, INSTANCE_CACHE);
        };
    }, []);
    const formatQuery = (0, react_1.useCallback)(() => {
        if (monacoRef.current) {
            monacoRef.current.getAction('editor.action.formatDocument').run();
        }
    }, []);
    const onSqlBlur = (text) => {
        onChange && onChange(text, false);
        onBlur && onBlur(text);
    };
    return (react_1.default.createElement("div", { style: { width } },
        react_1.default.createElement(ui_1.CodeEditor, { height: height || '240px', 
            // -2px to compensate for borders width
            width: width ? `${width - 2}px` : undefined, language: id, value: query, onBlur: onSqlBlur, showMiniMap: false, showLineNumbers: true, 
            // Using onEditorDidMount instead of onBeforeEditorMount to support Grafana < 8.2.x
            onEditorDidMount: (editor, m) => {
                monacoRef.current = editor;
                editor.onDidChangeModelContent((e) => {
                    const text = editor.getValue();
                    if (onChange) {
                        onChange(text, false);
                    }
                });
                editor.addCommand(m.KeyMod.CtrlCmd | m.KeyCode.Enter, () => {
                    const text = editor.getValue();
                    if (onChange) {
                        onChange(text, true);
                    }
                });
                editor.onKeyUp((e) => {
                    // keyCode 84 is . (DOT)
                    if (e.keyCode === 84) {
                        editor.trigger(commands_1.TRIGGER_SUGGEST.id, commands_1.TRIGGER_SUGGEST.id, {});
                    }
                });
                (0, exports.registerLanguageAndSuggestions)(m, language, id);
            } }),
        children && children({ formatQuery })));
};
exports.SQLEditor = SQLEditor;
// There's three ways to define Monaco language:
// 1. Leave language.id empty or set it to 'sql'. This will load a standard sql language definition, including syntax highlighting and tokenization for
// common Grafana entities such as macros and template variables
// 2. Provide a custom language and load it via the async LanguageDefinition.loader callback
// 3. Specify a language.id that exists in the Monaco language registry. A custom completion item provider can still be provided.
// If not, the standard SQL completion item provider will be used. See available languages here: https://github.com/microsoft/monaco-editor/tree/main/src/basic-languages
// If a custom language is specified, its LanguageDefinition will be merged with the LanguageDefinition for standard SQL. This allows the consumer to only
// override parts of the LanguageDefinition, such as for example the completion item provider.
const resolveLanguage = (monaco, languageDefinitionProp) => {
    if ((languageDefinitionProp === null || languageDefinitionProp === void 0 ? void 0 : languageDefinitionProp.id) !== STANDARD_SQL_LANGUAGE && !languageDefinitionProp.loader) {
        (0, debugger_1.sqlEditorLog)(`Loading language '${languageDefinitionProp === null || languageDefinitionProp === void 0 ? void 0 : languageDefinitionProp.id}' from Monaco registry`, false);
        const allLangs = monaco.languages.getLanguages();
        const custom = allLangs.find(({ id }) => id === (languageDefinitionProp === null || languageDefinitionProp === void 0 ? void 0 : languageDefinitionProp.id));
        if (!custom) {
            throw Error(`Unknown Monaco language ${languageDefinitionProp === null || languageDefinitionProp === void 0 ? void 0 : languageDefinitionProp.id}`);
        }
        return Object.assign(Object.assign({ completionProvider: standardSQLCompletionItemProvider_1.getStandardSQLCompletionProvider }, custom), languageDefinitionProp);
    }
    return Object.assign(Object.assign({}, definition_1.default), languageDefinitionProp);
};
const registerLanguageAndSuggestions = (monaco, l, lid) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
    const languageDefinition = resolveLanguage(monaco, l);
    if (!languageDefinition.loader) {
        return;
    }
    const { language, conf } = yield languageDefinition.loader(monaco);
    monaco.languages.register({ id: lid });
    monaco.languages.setMonarchTokensProvider(lid, Object.assign({}, language));
    monaco.languages.setLanguageConfiguration(lid, Object.assign({}, conf));
    if (languageDefinition.formatter) {
        monaco.languages.registerDocumentFormattingEditProvider(lid, {
            provideDocumentFormattingEdits: (model) => {
                var _a;
                const formatted = (_a = l.formatter) === null || _a === void 0 ? void 0 : _a.call(l, model.getValue());
                return [
                    {
                        range: model.getFullModelRange(),
                        text: formatted || '',
                    },
                ];
            },
        });
    }
    if (languageDefinition.completionProvider) {
        const customProvider = languageDefinition.completionProvider(monaco, language);
        extendStandardRegistries(l.id, lid, customProvider);
        const languageSuggestionsRegistries = LANGUAGES_CACHE.get(l.id);
        const instanceSuggestionsRegistry = INSTANCE_CACHE.get(lid);
        const completionProvider = (model, position, context, token) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
            const currentToken = (0, linkedTokenBuilder_1.linkedTokenBuilder)(monaco, model, position, lid);
            const statementPosition = (0, getStatementPosition_1.getStatementPosition)(currentToken, languageSuggestionsRegistries.positionResolvers);
            const kind = (0, getSuggestionKind_1.getSuggestionKinds)(statementPosition, languageSuggestionsRegistries.suggestionKinds);
            (0, debugger_1.sqlEditorLog)('Statement position', false, statementPosition);
            (0, debugger_1.sqlEditorLog)('Suggestion kinds', false, kind);
            const ctx = {
                position,
                currentToken,
                statementPosition,
                kind,
                range: monaco.Range.fromPositions(position),
            };
            const stdSuggestions = yield (0, getStandardSuggestions_1.getStandardSuggestions)(monaco, currentToken, kind, ctx, instanceSuggestionsRegistry);
            return {
                suggestions: stdSuggestions,
            };
        });
        monaco.languages.registerCompletionItemProvider(lid, Object.assign(Object.assign({}, customProvider), { provideCompletionItems: completionProvider }));
    }
});
exports.registerLanguageAndSuggestions = registerLanguageAndSuggestions;
function extendStandardRegistries(id, lid, customProvider) {
    var _a;
    if (!LANGUAGES_CACHE.has(id)) {
        initializeLanguageRegistries(id);
    }
    const languageRegistries = LANGUAGES_CACHE.get(id);
    if (!INSTANCE_CACHE.has(lid)) {
        INSTANCE_CACHE.set(lid, new data_1.Registry((0, standardSuggestionsRegistry_1.initStandardSuggestions)(languageRegistries.functions, languageRegistries.operators, languageRegistries.macros)));
    }
    const instanceSuggestionsRegistry = INSTANCE_CACHE.get(lid);
    if (customProvider.supportedFunctions) {
        for (const func of customProvider.supportedFunctions()) {
            const exists = languageRegistries.functions.getIfExists(func.id);
            if (!exists) {
                languageRegistries.functions.register(func);
            }
        }
    }
    if (customProvider.supportedOperators) {
        for (const op of customProvider.supportedOperators()) {
            const exists = languageRegistries.operators.getIfExists(op.id);
            if (!exists) {
                languageRegistries.operators.register(Object.assign(Object.assign({}, op), { name: op.id }));
            }
        }
    }
    if (customProvider.supportedMacros) {
        for (const macro of customProvider.supportedMacros()) {
            const exists = languageRegistries.macros.getIfExists(macro.id);
            if (!exists) {
                languageRegistries.macros.register(Object.assign(Object.assign({}, macro), { name: macro.id }));
            }
        }
    }
    if (customProvider.customStatementPlacement) {
        for (const placement of customProvider.customStatementPlacement()) {
            const exists = languageRegistries.positionResolvers.getIfExists(placement.id);
            if (!exists) {
                languageRegistries.positionResolvers.register(Object.assign(Object.assign({}, placement), { id: placement.id, name: placement.id }));
                languageRegistries.suggestionKinds.register({
                    id: placement.id,
                    name: placement.id,
                    kind: [],
                });
            }
            else {
                // Allow extension to the built-in placement resolvers
                const origResolve = exists.resolve;
                exists.resolve = (...args) => {
                    const ext = placement.resolve(...args);
                    if (placement.overrideDefault) {
                        return ext;
                    }
                    const orig = origResolve(...args);
                    return orig || ext;
                };
            }
        }
    }
    if (customProvider.customSuggestionKinds) {
        for (const kind of customProvider.customSuggestionKinds()) {
            (_a = kind.applyTo) === null || _a === void 0 ? void 0 : _a.forEach((applyTo) => {
                const exists = languageRegistries.suggestionKinds.getIfExists(applyTo);
                if (exists) {
                    // avoid duplicates
                    if (exists.kind.indexOf(kind.id) === -1) {
                        exists.kind.push(kind.id);
                    }
                }
            });
            if (kind.overrideDefault) {
                const stbBehavior = instanceSuggestionsRegistry.get(kind.id);
                if (stbBehavior !== undefined) {
                    stbBehavior.suggestions = kind.suggestionsResolver;
                    continue;
                }
            }
            instanceSuggestionsRegistry.register({
                id: kind.id,
                name: kind.id,
                suggestions: kind.suggestionsResolver,
            });
        }
    }
    if (customProvider.schemas) {
        const stbBehavior = instanceSuggestionsRegistry.get(types_1.SuggestionKind.Schemas);
        const s = stbBehavior.suggestions;
        stbBehavior.suggestions = (ctx, m) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const standardSchemas = yield s(ctx, m);
            if (!customProvider.schemas) {
                return [...standardSchemas];
            }
            const customSchemas = yield customProvider.schemas.resolve();
            const customSchemaCompletionItems = customSchemas.map((x) => {
                var _a;
                return ({
                    label: x.name,
                    insertText: `${(_a = x.completion) !== null && _a !== void 0 ? _a : x.name}.`,
                    command: commands_1.TRIGGER_SUGGEST,
                    kind: types_1.CompletionItemKind.Module,
                    sortText: types_1.CompletionItemPriority.High,
                });
            });
            return [...standardSchemas, ...customSchemaCompletionItems];
        });
    }
    if (customProvider.tables) {
        const stbBehavior = instanceSuggestionsRegistry.get(types_1.SuggestionKind.Tables);
        const s = stbBehavior.suggestions;
        stbBehavior.suggestions = (ctx, m) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _b, _c, _d, _e, _f;
            const o = yield s(ctx, m);
            const tableToken = (0, tokenUtils_1.getTableToken)(ctx.currentToken);
            const tableNameParser = (_c = (_b = customProvider.tables) === null || _b === void 0 ? void 0 : _b.parseName) !== null && _c !== void 0 ? _c : tokenUtils_1.defaultTableNameParser;
            const tableIdentifier = tableNameParser(tableToken);
            const oo = ((_f = (yield ((_e = (_d = customProvider.tables) === null || _d === void 0 ? void 0 : _d.resolve) === null || _e === void 0 ? void 0 : _e.call(_d, tableIdentifier)))) !== null && _f !== void 0 ? _f : []).map((x) => {
                var _a;
                return ({
                    label: x.name,
                    // if no custom completion is provided it's safe to move cursor further in the statement
                    insertText: `${(_a = x.completion) !== null && _a !== void 0 ? _a : x.name}${x.completion === x.name ? ' $0' : ''}`,
                    insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                    command: commands_1.TRIGGER_SUGGEST,
                    kind: types_1.CompletionItemKind.Field,
                    sortText: types_1.CompletionItemPriority.MediumHigh,
                });
            });
            return [...o, ...oo];
        });
    }
    if (customProvider.columns) {
        const stbBehavior = instanceSuggestionsRegistry.get(types_1.SuggestionKind.Columns);
        const s = stbBehavior.suggestions;
        stbBehavior.suggestions = (ctx, m) => tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _g, _h, _j;
            const o = yield s(ctx, m);
            const tableToken = (0, tokenUtils_1.getTableToken)(ctx.currentToken);
            let tableIdentifier;
            const tableNameParser = (_h = (_g = customProvider.tables) === null || _g === void 0 ? void 0 : _g.parseName) !== null && _h !== void 0 ? _h : tokenUtils_1.defaultTableNameParser;
            if (tableToken && tableToken.value) {
                tableIdentifier = tableNameParser(tableToken);
            }
            let oo = [];
            if (tableIdentifier) {
                const columns = yield ((_j = customProvider.columns) === null || _j === void 0 ? void 0 : _j.resolve(tableIdentifier));
                oo = columns
                    ? columns.map((x) => {
                        var _a;
                        return ({
                            label: x.name,
                            insertText: (_a = x.completion) !== null && _a !== void 0 ? _a : x.name,
                            kind: types_1.CompletionItemKind.Field,
                            sortText: types_1.CompletionItemPriority.High,
                            detail: x.type,
                            documentation: x.description,
                        });
                    })
                    : [];
            }
            return [...o, ...oo];
        });
    }
}
/**
 * Initializes language specific registries that are treated as singletons
 */
function initializeLanguageRegistries(id) {
    if (!LANGUAGES_CACHE.has(id)) {
        LANGUAGES_CACHE.set(id, {
            functions: new data_1.Registry(),
            operators: new data_1.Registry(),
            suggestionKinds: new data_1.Registry(suggestionsKindRegistry_1.initSuggestionsKindRegistry),
            positionResolvers: new data_1.Registry(statementPositionResolversRegistry_1.initStatementPositionResolvers),
            macros: new data_1.Registry(),
        });
    }
    return LANGUAGES_CACHE.get(id);
}
//# sourceMappingURL=SQLEditor.js.map